import { Scroll } from "phosphor-react";
import {
    getActiveDeclarationType,
    type MandateDetails,
} from "../../../models/api/admin/mandateDetails";
import { convertDateStringToReadableString } from "../../../utils/convertDateStringToReadableString";
import ActionableStatus from "../../ActionableStatus";
import ActionableStatusTag from "../../ActionableStatus/ActionableStatusTag";
import useDeclarationStatusActions from "./useDeclarationStatusActions";
import { DeclarationType } from "../../../models/app/eDeclarations";
import { ApiDriverByIdResponse } from "../../../models/api/admin/driver";
import { DriverValidationTypes } from "../../../models/app/admin/company";
import { isUkLicence } from "../../../constants/countries";

export type DeclarationStatusCompleteProps = {
    mandateDetails: MandateDetails;
    driverData?: ApiDriverByIdResponse;
    validateByCompanyPreference?: DriverValidationTypes;
    isS2DUser: boolean;
    handleScrollToDocumentUpload: () => void;
};

const DeclarationStatusComplete: React.FC<DeclarationStatusCompleteProps> = ({
    mandateDetails,
    driverData,
    validateByCompanyPreference,
    isS2DUser,
    handleScrollToDocumentUpload,
}) => {
    const {
        declarationId,
        declarationStatus,
        declarationSigned,
        expiryDate,
        declarationReminderLastSentDate,
        hasInFlightDeclaration,
        licenceCountryOrigin,
    } = mandateDetails;
    const activeDeclarationType = getActiveDeclarationType(mandateDetails);

    const {
        sendNewEDeclarationAction,
        sendNewPaperDeclarationAction,
        resendEDeclarationAction,
        resendPaperDeclarationAction,
        switchToEDeclarationAction,
        switchToPaperDeclarationAction,
        uploadSignedDeclarationAction,
    } = useDeclarationStatusActions(
        declarationId,
        mandateDetails,
        !hasInFlightDeclaration,
        handleScrollToDocumentUpload,
        driverData
    );
    const statusIndicators = new Array(0);
    if (activeDeclarationType === "PaperDeclaration") {
        statusIndicators.push(
            <ActionableStatusTag theme="white" text="Paper" />
        );
    }
    if (declarationStatus === "Completed") {
        statusIndicators.push(<ActionableStatusTag theme="completed" />);
    }
    if (declarationStatus === "DueToExpire") {
        statusIndicators.push(<ActionableStatusTag theme="dueToExpire" />);
    }

    const sendStatusActions =
        activeDeclarationType === DeclarationType.EDeclaration
            ? [
                  hasInFlightDeclaration
                      ? resendEDeclarationAction
                      : sendNewEDeclarationAction,
                  ...(hasInFlightDeclaration
                      ? [switchToPaperDeclarationAction]
                      : []),
              ]
            : [
                  hasInFlightDeclaration
                      ? resendPaperDeclarationAction
                      : sendNewPaperDeclarationAction,
                  ...(hasInFlightDeclaration &&
                  isUkLicence(licenceCountryOrigin)
                      ? [switchToEDeclarationAction]
                      : []),
                  ...(isS2DUser && hasInFlightDeclaration
                      ? [uploadSignedDeclarationAction]
                      : []),
              ];

    const StatusContent = () => {
        return (
            <div className="flex flex-col text-sm">
                <span>
                    Declaration signed :{" "}
                    {convertDateStringToReadableString(declarationSigned)}
                </span>
                <span>
                    Expires: {convertDateStringToReadableString(expiryDate)}
                </span>
                {declarationStatus === "DueToExpire" && 
                <span>
                    {hasInFlightDeclaration
                        ? "New declaration sent: "
                        : "Last sent: "}
                    {convertDateStringToReadableString(declarationReminderLastSentDate)}
                </span>
                }
                
            </div>
        );
    };

    return (
        <ActionableStatus
            title="Declaration status"
            theme="grey"
            actionable
            statusIndicators={statusIndicators}
            actions={sendStatusActions}
            hasEmailAddress={!!driverData?.emailAddress}
            declarationComplete={true}
            licenceCountryOrigin={licenceCountryOrigin ?? undefined}
            driverData={driverData}
            validateByCompanyPreference={validateByCompanyPreference}
        >
            <div className="flex items-center">
                <div className="pr-3">
                    <Scroll size={24} weight="duotone" />
                </div>
                <StatusContent />
            </div>
        </ActionableStatus>
    );
};

export default DeclarationStatusComplete;
